import { isDateInFuture } from '@apply/ui';
import { usePropertyStructureType } from '../../../queries/PropertyFields/TopLevelFields';
import { usePropertyAcquisitionDate } from '../../../queries/PropertyFields/NonRentalCoreFields';
import {
    usePropertyGeneralContractorWillWork,
    usePropertyIsAcquisitionFinancing,
} from '../../../queries/PropertyFields/NonRentalFlagFields';
import { usePropertyContactIsPrimary } from '../../../queries/PropertyFields/NonRentalContactFields';
import { usePropertySpecSheetFile } from '../../../queries/PropertyFileFields/NonRentalFiles';

import { ShowIf } from '../../components/ShowIf';
import { PropertyAddress } from '../components/PropertyAddress/PropertyAddress';
import { LegalDescription } from '../components/LegalDescription';
import { LandSize } from '../components/LandSize';
import { YearBuilt } from '../components/YearBuilt';
import { SiteBuiltOrManufactured } from '../components/SiteBuiltOrManufactored';
import { HoaDues } from '../components/HoaDues';
import { UndergroundStorageTanks } from '../components/UndergroundStorageTanks';
import { DateOfAcquisition } from '../components/DateOfAcquisition';
import { ExpandingConditionedSqFt } from '../components/ExpandingConditionedSqFt';
import { MakingStructuralChanges } from '../components/MakingStructuralChanges';
import { Foreclosure } from '../components/Foreclosure';
import { GeneralContractor } from '../components/GeneralContractor';
import { GeneralContractorCompany } from '../components/GeneralContractorCompany';
import { GeneralContractorFile } from '../../components/FileOrFormQuestions/questions/GeneralContractorFile';
import { OnsitePropertyContact } from '../components/OnsitePropertyContact';
import { OnsitePropertyContactInformation } from '../components/OnsitePropertyContactInformation/OnsitePropertyContactInformation';
import { AcquisitionFinancing } from '../components/AcqusitionFinancing';
import { CurrentDebt } from '../components/CurrentDebt';
import { IntendedWork } from '../components/IntendedWork';
import { BudgetFileFixFlip } from '../../components/FileOrFormQuestions/questions/BudgetFileFixFlip';
import { SpecSheetFile } from '../../components/FileOrFormQuestions/questions/SpecSheetFile';
import { ConstructionStarted } from '../components/ConstructionStarted';
import { ExpectedListingPrice } from '../components/ExpectedListingPrice';
import { TargetLoanRequest } from '../components/TargetLoanRequest';
import { StructureAndUnits } from '../components/StructureAndUnits/StructureAndUnits';
import { ProvideLaterAdvisory } from '../components/ProvideLaterAdvisory';
import { DropDownBoxComponentSpacing, DropDownBoxLabelSize } from '@apply/components/src/components/controls/DropDownBox';

export interface FixFlipProps {
    disableEditFormOptionBudget: boolean;
    disableEditFormOptionSpecSheet: boolean;
    propertyGuid: string;
    isReview?: boolean;
}

export const FixFlip: React.FC<FixFlipProps> = ({
    disableEditFormOptionBudget,
    disableEditFormOptionSpecSheet,
    propertyGuid,
    isReview = false,
}) => {
    const [acquisitionDate] = usePropertyAcquisitionDate(propertyGuid);
    const [generalContractorWillWork] = usePropertyGeneralContractorWillWork(propertyGuid);
    const [contactIsPrimary] = usePropertyContactIsPrimary(propertyGuid);
    const [isAcquisitionFinancing] = usePropertyIsAcquisitionFinancing(propertyGuid);
    const [structureType] = usePropertyStructureType(propertyGuid);
    const { handleMode: specSheetHandle } = usePropertySpecSheetFile(propertyGuid);

    return (
        <>
            <StructureAndUnits
                propertyGuid={propertyGuid}
                isReview={isReview}
            />
            <PropertyAddress
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <ShowIf showIf={!isReview}>
                <br />
                <br />
                <div className='pageSubHeader'>Additional Information</div>
            </ShowIf>
            <LegalDescription
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <LandSize
                propertyGuid={propertyGuid}
                isReview={isReview}
            />
            <YearBuilt
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <SiteBuiltOrManufactured
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
                componentSizing={DropDownBoxComponentSpacing.LargeSpacing}
                labelSize={DropDownBoxLabelSize.Medium}
            />
            <HoaDues
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <br />
            <DateOfAcquisition
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <Foreclosure
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <ShowIf showIf={isDateInFuture(acquisitionDate) === true}>
                <AcquisitionFinancing
                    propertyGuid={propertyGuid}
                    required={true}
                    isReview={isReview}
                />
                <ShowIf showIf={isAcquisitionFinancing === false}>
                    <CurrentDebt
                        propertyGuid={propertyGuid}
                        required={true}
                        isReview={isReview}
                    />
                </ShowIf>
            </ShowIf>
            <ShowIf showIf={isDateInFuture(acquisitionDate) === false}>
                <CurrentDebt
                    propertyGuid={propertyGuid}
                    required={true}
                    isReview={isReview}
                />
            </ShowIf>
            <GeneralContractor
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <ShowIf showIf={generalContractorWillWork}>
                <br />
                <br />
                <div className='pageSubHeader'>General Contractor</div>
                <GeneralContractorCompany
                    propertyGuid={propertyGuid}
                    required={true}
                    isReview={isReview}
                />
                <GeneralContractorFile
                    propertyGuid={propertyGuid}
                    required={true}
                    isReview={isReview}
                />
            </ShowIf>

            <ShowIf showIf={!generalContractorWillWork}>
                <OnsitePropertyContact
                    propertyGuid={propertyGuid}
                    required={true}
                    isReview={isReview}
                />
            </ShowIf>
            <ShowIf showIf={!generalContractorWillWork && contactIsPrimary === false}>
                <OnsitePropertyContactInformation
                    propertyGuid={propertyGuid}
                    required={true}
                    isReview={isReview}
                />
            </ShowIf>
            <ExpandingConditionedSqFt
                propertyGuid={propertyGuid}
                isReview={isReview}
            />
            <MakingStructuralChanges
                propertyGuid={propertyGuid}
                isReview={isReview}
            />
            <IntendedWork
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <UndergroundStorageTanks
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <ShowIf showIf={!isReview}>
                <br />
                <br />
                <div className='pageSubHeader'>Additional Files</div>
            </ShowIf>
            <BudgetFileFixFlip
                disableEditFormOptionBudget={disableEditFormOptionBudget}
                required={true}
                propertyGuid={propertyGuid}
                isReview={isReview}
            />
            <SpecSheetFile
                disableEditFormOptionSpecSheet={disableEditFormOptionSpecSheet}
                required={true}
                propertyGuid={propertyGuid}
                isReview={isReview}
            />
            <ShowIf showIf={!isReview && specSheetHandle === 'willnotprovide'}>
                <p style={{ color: 'red', marginLeft: 20 }}>
                    NOTE: Without this detail, the appraiser is likely to assume the lowest grade materials.
                </p>
            </ShowIf>
            <ProvideLaterAdvisory
                propertyGuid={propertyGuid}
                isReview={isReview}
            />
            <ConstructionStarted
                label='Have you started work?'
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <ExpectedListingPrice
                structureType={structureType}
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <TargetLoanRequest
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
        </>
    );
};
